import { useCallback, useMemo, useState } from 'react';
import { PortableTextBlock } from '@sanity/types';
import type { ArbitraryTypedObject } from '@portabletext/types';
import Image from 'next/image';
import { getSanityImageUrl } from '@/libs/sanity/client';
import { PortableTextWrapper } from '@/components/PortableTextWrapper';
import VideoModal from '@/components/VideoModal';
import { Video } from '@/components/LeftTextRightImageAndVideo';
import { BiExpandAlt } from 'react-icons/bi';
import { SlArrowRightCircle } from 'react-icons/sl';
import { BlurInH2 } from '@/components/eldora-ui/blur-in-h2';
import { PortableText } from '@portabletext/react';
import {
  Accordion,
  AccordionContainer,
  AccordionItem,
  AccordionWrapper,
  AccordionHeader,
  AccordionPanel,
} from '@/components/ui/accordion';
import { TABLET_SCREEN_SIZE } from '@/utils/constants';
import { useMediaQuery } from 'usehooks-ts';

type TabItem = {
  name?: string;
  title?: string;
  content?:
    | (PortableTextBlock | ArbitraryTypedObject)
    | (PortableTextBlock | ArbitraryTypedObject)[];
  image?: any;
  video?: Video;
};

type TabListProps = {
  anchor?: string;
  title?: string;
  firstTabItemsTitle?: string;
  secondTabItemsTitle?: string;
  isFullWidth?: boolean;
  description?:
    | (PortableTextBlock | ArbitraryTypedObject)
    | (PortableTextBlock | ArbitraryTypedObject)[];
  secondTabItemsDescription?:
    | (PortableTextBlock | ArbitraryTypedObject)
    | (PortableTextBlock | ArbitraryTypedObject)[];
  secondTabItemsIntroduction?:
    | (PortableTextBlock | ArbitraryTypedObject)
    | (PortableTextBlock | ArbitraryTypedObject)[];
  name?: string;
  tabItems?: TabItem[];
  middleImage?: any;
  secondTabItems?: TabItem[];
  rightImage?: any;
  bottomImage?: any;
  video?: Video;
  bottomContent?:
    | (PortableTextBlock | ArbitraryTypedObject)
    | (PortableTextBlock | ArbitraryTypedObject)[];
};

// Extract to a Tab component
const Tab = ({
  name,
  isSelected,
  onClick,
}: {
  name: string;
  isSelected: boolean;
  onClick: () => void;
}) => (
  <li
    role="tab"
    aria-selected={isSelected}
    tabIndex={0}
    onClick={onClick}
    onKeyDown={(e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        onClick();
      }
    }}
    className={`flex items-end justify-center relative cursor-pointer leading-tight py-4 border-b-2 ${
      isSelected
        ? 'border-primary'
        : 'border-transparent hover:text-gray-700 hover:border-gray-300 text-gray-500'
    }`}
  >
    <span className="max-w-40 block mx-auto">{name}</span>
    {isSelected && (
      <>
        <div className="absolute left-0 right-0 bottom-0 h-[1.5px] bg-primary rounded" />
        <div className="absolute left-1/2 transform -translate-x-1/2 bottom-[-8px] w-0 h-0 border-l-[8px] border-l-transparent border-r-[8px] border-r-transparent border-t-[8px] border-t-primary" />
      </>
    )}
  </li>
);

// Extract to a TabPanel component
const TabPanel = ({
  content,
  video,
  image,
}: {
  content:
    | PortableTextBlock
    | ArbitraryTypedObject
    | (PortableTextBlock | ArbitraryTypedObject)[];
  video?: Video;
  image?: any;
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);

  const renderVideoButton = (video: Video) => {
    const isDefaultLabel =
      !video.buttonLabel || video.buttonLabel === 'Enlarge Video';

    return (
      <div
        className={`mt-4 justify-start text-primary gap-x-1 items-center cursor-pointer ${isDefaultLabel ? 'flex flex-row' : ''}`}
        onClick={() => setIsModalOpen(true)}
      >
        {video.buttonLabel && (
          <>
            {isDefaultLabel && <BiExpandAlt className="font-bold" />}
            <span>{video.buttonLabel || 'Enlarge Video'}</span>
            {!isDefaultLabel && (
              <SlArrowRightCircle
                className="text-primary mt-1 block"
                size={18}
              />
            )}
          </>
        )}
        {video.buttonLongLabel && (
          <>
            <div className="text-primary">
              <PortableText value={video.buttonLongLabel} />
              <SlArrowRightCircle
                className="text-primary mt-1 block"
                size={18}
              />
            </div>
          </>
        )}
      </div>
    );
  };

  return (
    <div className="mt-6" role="tabpanel">
      <div className="w-full lg:max-w-3xl xl:max-w-4xl">
        <PortableTextWrapper value={content} />
      </div>
      {image && (
        <div className="mt-2">
          <Image
            src={getSanityImageUrl(image)}
            alt="Image"
            className="object-cover"
            loading="lazy"
            width={1400}
            height={2000}
          />
        </div>
      )}
      {video && video.coverImage && (
        <>
          <div className="mt-2">
            <Image
              src={getSanityImageUrl(video.coverImage)}
              alt="Image"
              className="object-cover"
              loading="lazy"
              width={1400}
              height={2000}
            />
          </div>
          {renderVideoButton(video)}
        </>
      )}
      {video && (
        <VideoModal
          isModalOpen={isModalOpen}
          setIsModalOpen={setIsModalOpen}
          video={video}
        />
      )}
    </div>
  );
};

export default function TabList({
  title,
  firstTabItemsTitle,
  secondTabItemsTitle,
  isFullWidth,
  description,
  secondTabItemsDescription,
  secondTabItemsIntroduction,
  rightImage,
  middleImage,
  bottomImage,
  bottomContent,
  anchor,
  video,
  tabItems = [],
  secondTabItems = [],
}: TabListProps) {
  const [selectedTab, setSelectedTab] = useState(tabItems[0]?.name ?? '');
  const [selectedSecondaryTab, setSelectedSecondaryTab] = useState(
    secondTabItems[0]?.name ?? ''
  );
  const [isModalOpen, setIsModalOpen] = useState(false);

  const isTabletScreen = useMediaQuery(`(max-width: ${TABLET_SCREEN_SIZE}px)`);

  const selectedTabContent = useMemo(() => {
    const tab = tabItems.find((tab) => tab.name === selectedTab);
    return tab
      ? { content: tab.content, image: tab.image, video: tab.video }
      : { content: [], image: undefined, video: undefined };
  }, [selectedTab, tabItems]);
  const selectedSecondaryTabContent = useMemo(() => {
    const tab = secondTabItems.find((tab) => tab.name === selectedSecondaryTab);
    return tab
      ? { content: tab.content, image: tab.image, video: tab.video }
      : { content: [], image: undefined, video: undefined };
  }, [selectedSecondaryTab, secondTabItems]);

  const handleTabClick = useCallback(
    (tabName: string, isSecondary: boolean) => {
      if (isSecondary) {
        setSelectedSecondaryTab(tabName);
      } else {
        setSelectedTab(tabName);
      }
    },
    []
  );

  const renderTabs = useCallback(
    (items: TabItem[], isSecondary: boolean) => {
      const classNames = {
        1: 'max-w-full',
        2: 'max-w-xl',
        3: 'max-w-4xl',
        default: 'max-w-full',
      };

      const className = `flex flex-auto flex-wrap justify-between gap-x-4 ${classNames[items.length as keyof typeof classNames] || classNames.default}`;

      return (
        <ul className={className} role="tablist">
          {items.map((tab) => {
            const isSelected = isSecondary
              ? selectedSecondaryTab === tab.name
              : selectedTab === tab.name;
            return (
              <Tab
                key={tab.name}
                name={tab.name!}
                isSelected={isSelected}
                onClick={() => handleTabClick(tab.name!, isSecondary)}
              />
            );
          })}
        </ul>
      );
    },
    [handleTabClick, selectedTab, selectedSecondaryTab]
  );

  const renderTabContent = useCallback(
    (content: any, video: Video, image: any) => {
      return <TabPanel content={content} video={video} image={image} />;
    },
    []
  );

  const renderVideoButton = (video: Video) => {
    const isDefaultLabel =
      !video.buttonLabel || video.buttonLabel === 'Enlarge Video';

    return (
      <div
        className={`mt-2 justify-start text-primary gap-x-1 items-center cursor-pointer ${isDefaultLabel ? 'flex flex-row' : ''}`}
        onClick={() => setIsModalOpen(true)}
      >
        {video.buttonLabel && (
          <>
            {isDefaultLabel && <BiExpandAlt className="font-bold" />}
            <span>{video.buttonLabel || 'Enlarge Video'}</span>
            {!isDefaultLabel && (
              <SlArrowRightCircle
                className="text-primary mt-1 block"
                size={18}
              />
            )}
          </>
        )}
        {video.buttonLongLabel && (
          <>
            <div className="text-primary">
              <PortableText value={video.buttonLongLabel} />
              <SlArrowRightCircle
                className="text-primary mt-1 block"
                size={18}
              />
            </div>
          </>
        )}
      </div>
    );
  };

  return (
    <section id={anchor} className="max-w-8xl mx-auto mt-8 scroll-mt-[150px]">
      <BlurInH2>{title}</BlurInH2>
      <div className="flex flex-col md:flex-row gap-6">
        {description && (
          <div
            className={`w-full ${
              isFullWidth ? 'prose xl:max-w-8xl' : 'xl:max-w-3xl'
            } ${rightImage ? 'md:w-7/12' : ''}`}
          >
            <PortableTextWrapper value={description} />
          </div>
        )}
        {rightImage && (
          <div className="w-full md:w-5/12">
            <Image
              src={getSanityImageUrl(rightImage)}
              alt={title || 'Tab image'}
              className="h-full object-cover w-full"
              width={614}
              height={533}
              loading="lazy"
            />
          </div>
        )}
        {video && video.coverImage && (
          <div>
            <div className="">
              <Image
                src={getSanityImageUrl(video.coverImage)}
                alt="Image"
                className="object-cover"
                loading="lazy"
                width={1000}
                height={2000}
              />
            </div>
            {renderVideoButton(video)}
          </div>
        )}
      </div>
      {/* Tabs */}
      {firstTabItemsTitle && (
        <h5 className="text-lg mt-8 mb-4 font-medium leading-[1.25em]">
          {firstTabItemsTitle}
        </h5>
      )}
      {!isTabletScreen && (
        <>
          <div className="border-b border-gray-200">
            {renderTabs(tabItems, false)}
          </div>
          {renderTabContent(
            selectedTabContent.content,
            selectedTabContent.video,
            selectedTabContent.image
          )}
        </>
      )}
      {isTabletScreen && (
        <AccordionContainer className="grid-cols-1 mt-4">
          <AccordionWrapper>
            <Accordion>
              {tabItems.map((tab) => (
                <AccordionItem key={tab.name} value={tab.name}>
                  <AccordionHeader leftIcon>
                    <span className="2xl:text-base text-sm">{tab.name}</span>
                  </AccordionHeader>

                  <AccordionPanel>
                    <PortableTextWrapper value={tab.content} />
                    {tab.image && (
                      <Image
                        src={getSanityImageUrl(tab.image)}
                        alt="Image"
                        className="object-cover"
                        width={1400}
                        height={2000}
                        loading="lazy"
                      />
                    )}
                    {tab.video && tab.video.coverImage && (
                      <>
                        <div className="mt-2">
                          <Image
                            src={getSanityImageUrl(tab.video.coverImage)}
                            alt="Image"
                            className="object-cover"
                            width={1400}
                            height={2000}
                            loading="lazy"
                          />
                        </div>
                        {renderVideoButton(tab.video)}
                      </>
                    )}
                    {tab.video && (
                      <VideoModal
                        isModalOpen={isModalOpen}
                        setIsModalOpen={setIsModalOpen}
                        video={tab.video}
                      />
                    )}
                  </AccordionPanel>
                </AccordionItem>
              ))}
            </Accordion>
          </AccordionWrapper>
        </AccordionContainer>
      )}

      {middleImage && (
        <div className="w-full mt-8">
          <Image
            src={getSanityImageUrl(middleImage)}
            alt={title || 'Tab image'}
            className="h-full object-cover w-full min-h-64"
            width={1440}
            height={442}
          />
        </div>
      )}
      {secondTabItemsIntroduction && (
        <div
          className={`w-full mt-8 xl:max-w-3xl ${rightImage ? 'md:w-7/12' : ''}`}
        >
          <PortableTextWrapper value={secondTabItemsIntroduction} />
        </div>
      )}
      {secondTabItemsTitle && (
        <h5 className="text-lg mt-12 mb-4 font-medium leading-[1.25em]">
          {secondTabItemsTitle}
        </h5>
      )}
      {!isTabletScreen && (
        <>
          {(secondTabItems && secondTabItems.length) > 0 && (
            <>
              <div className="border-b border-gray-200">
                {renderTabs(secondTabItems, true)}
              </div>

              {renderTabContent(
                selectedSecondaryTabContent,
                selectedSecondaryTabContent.video,
                selectedSecondaryTabContent.image
              )}
            </>
          )}
          {secondTabItemsDescription && (
            <div
              className={`w-full mt-8 xl:max-w-3xl ${rightImage ? 'md:w-7/12' : ''}`}
            >
              <PortableTextWrapper value={secondTabItemsDescription} />
            </div>
          )}
        </>
      )}
      {isTabletScreen && (
        <>
          {(secondTabItems && secondTabItems.length) > 0 && (
            <>
              <AccordionContainer className="grid-cols-1">
                <AccordionWrapper>
                  <Accordion>
                    {secondTabItems.map((tab) => (
                      <AccordionItem key={tab.name} value={tab.name}>
                        <AccordionHeader leftIcon>
                          <span className="2xl:text-base text-sm">
                            {tab.name}
                          </span>
                        </AccordionHeader>

                        <AccordionPanel>
                          <PortableTextWrapper value={tab.content} />
                          {tab.image && (
                            <Image
                              src={getSanityImageUrl(tab.image)}
                              alt="Image"
                              className="object-cover"
                              width={1400}
                              height={2000}
                              loading="lazy"
                            />
                          )}
                          {tab.video && video.coverImage && (
                            <>
                              <div className="mt-2">
                                <Image
                                  src={getSanityImageUrl(tab.video.coverImage)}
                                  alt="Image"
                                  className="object-cover"
                                  width={1400}
                                  height={2000}
                                  loading="lazy"
                                />
                              </div>
                              {renderVideoButton(tab.video)}
                            </>
                          )}
                          {tab.video && (
                            <VideoModal
                              isModalOpen={isModalOpen}
                              setIsModalOpen={setIsModalOpen}
                              video={tab.video}
                            />
                          )}
                        </AccordionPanel>
                      </AccordionItem>
                    ))}
                  </Accordion>
                </AccordionWrapper>
              </AccordionContainer>
            </>
          )}
          {secondTabItemsDescription && (
            <div
              className={`w-full mt-8 xl:max-w-3xl ${rightImage ? 'md:w-7/12' : ''}`}
            >
              <PortableTextWrapper value={secondTabItemsDescription} />
            </div>
          )}
        </>
      )}
      {bottomImage && (
        <div className="w-full mt-8">
          <Image
            src={getSanityImageUrl(bottomImage)}
            alt={title || 'Tab image'}
            className="h-full object-cover w-full min-h-64"
            width={1440}
            height={567}
            loading="lazy"
          />
        </div>
      )}
      {video && (
        <VideoModal
          isModalOpen={isModalOpen}
          setIsModalOpen={setIsModalOpen}
          video={video}
        />
      )}
      {bottomContent && (
        <div className="mt-8">
          <PortableTextWrapper value={bottomContent} />
        </div>
      )}
    </section>
  );
}
